import type { JSX } from 'react';
import { Link } from 'react-router';
import { Button, Card, StatusIndicator } from '@viniciomendesr/rayrovisk-design-system';
import { grantsContent } from '../api/client';
import { useMe } from '../api/queries';
import { AccessStateScreen } from '../access/AccessState';

/**
 * `Montador de prompt` — the assisted layer, which does not exist yet.
 *
 * It was called `Gerar prompt` until 2026-08-06, when the assembler took that
 * name back, and `Refinar com IA` for the hour after that. The owner settled on
 * `Montador de prompt`.
 *
 * **The heading and the paragraph under it disagree, and that is known.** The
 * copy below describes an AI that rewrites a prompt with her; the name says the
 * thing that assembles one, which is the screen at `/gerador/montar`. Nothing
 * is broken by it today because there is nothing here to use, but this screen
 * cannot ship with both — either the name follows the feature or the feature
 * follows the name.
 *
 * The route exists anyway, because the navigation entry that points here is
 * drawn rather than hidden, and an entry that leads to a blank page is worse
 * than one that leads to a sentence.
 *
 * **The ownership check below is done on the client, and that is not a
 * departure from "appearance, never authority".** Every other screen gets its
 * answer from a 403 because every other screen asks the service for something.
 * This one asks for nothing — there is no data yet to withhold — so there is no
 * 403 to catch, and without the check a pack buyer would land on a description
 * of a feature she was never sold instead of on its price. Nothing is being
 * protected here; a message is being chosen. The moment this screen has content
 * to fetch, the gate moves to the server with it.
 *
 * For everyone who does own it, the copy neither apologises nor sells: she paid
 * for this, so the only true thing left to say is that this part is not
 * finished. No date, on purpose — a date printed here is a promise the roadmap
 * has not made, and this is exactly where a broken one would be remembered.
 */
export function AssistedPromptScreen(): JSX.Element | null {
  const me = useMe();

  if (me.isLoading) {
    return null;
  }

  const state = me.data?.products['prompt-generator'];
  if (state && !grantsContent(state)) {
    return <AccessStateScreen state={state} />;
  }

  return (
    <div style={{ maxWidth: 620, padding: '48px 0' }}>
      <Card layer="functional" elevation="raised" padding="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <h1 style={{ margin: 0, font: '400 28px/1.15 var(--rvk-font-expressive)' }}>
            Montador de prompt
          </h1>
          <StatusIndicator status="idle" label="Ainda estamos construindo esta parte." />
          <p
            style={{
              margin: 0,
              font: '400 14.5px/1.6 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)',
              textWrap: 'pretty'
            }}
          >
            Aqui a IA vai reescrever o prompt junto com você: afinar o tom, cortar o que ficou
            genérico, testar outro ângulo do mesmo assunto. Está incluído no que você já comprou, e
            não vai custar nada a mais quando abrir.
          </p>
          <p
            style={{
              margin: 0,
              font: '400 14.5px/1.6 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)',
              textWrap: 'pretty'
            }}
          >
            Enquanto isso, gerar o prompt a partir do seu perfil já funciona e não gasta nada.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/gerador/montar">
              <Button variant="primary" size="sm">
                Gerar um prompt
              </Button>
            </Link>
            <a href="mailto:acesso@roteirovisk.com?subject=Montador%20de%20prompt">
              <Button variant="ghost" size="sm">
                Quero avisar o que preciso
              </Button>
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
}
